import { RangeHorizon } from '@chart/types/resample.types';
import type {
  Chart,
  Context,
  Country,
  Instrument,
  InstrumentElement,
  InstrumentList,
} from '@finos/fdc3';

// The generated TimeRangeObject types these as Date, but payloads arrive as
// ISO strings after JSON deserialization.
export interface Fdc3TimeRange {
  startTime?: Date | string;
  endTime?: Date | string;
}

const HORIZON_DAYS: [RangeHorizon, number][] = [
  [RangeHorizon.OneDay, 1],
  [RangeHorizon.OneWeek, 7],
  [RangeHorizon.TwoWeeks, 14],
  [RangeHorizon.OneMonth, 30],
  [RangeHorizon.TwoMonths, 61],
  [RangeHorizon.ThreeMonths, 91],
  [RangeHorizon.SixMonths, 182],
  [RangeHorizon.OneYear, 365],
  [RangeHorizon.ThreeYears, 1095],
  [RangeHorizon.FiveYears, 1825],
  [RangeHorizon.TenYears, 3650],
];

const MS_PER_DAY = 86_400_000;

// The entity resolver only knows bare exchange tickers; a RIC's exchange
// suffix (AAPL.OQ, VOD.L, 7203.T) must be dropped before lookup.
export const ricToTicker = (ric: string): string =>
  ric.split('.')[0]?.trim() ?? '';

export const instrumentToResolverInput = (
  instrument: Instrument | InstrumentElement
): string | undefined => {
  const ticker = instrument.id.ticker?.trim();

  if (ticker) {
    return ticker;
  }

  return ricToTicker(instrument.id.RIC ?? '') || undefined;
};

export const contextToInstruments = (context: Context): InstrumentElement[] => {
  switch (context.type) {
    case 'fdc3.instrument':
      return [context as Instrument];
    case 'fdc3.instrumentList':
      return (context as InstrumentList).instruments ?? [];
    case 'fdc3.chart':
      return (context as Chart).instruments ?? [];
    default:
      return [];
  }
};

const toEpochMs = (value: Date | string | undefined): number | undefined => {
  if (value === undefined) {
    return undefined;
  }

  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : ms;
};

export const timeRangeToHorizon = (
  range: Fdc3TimeRange | undefined,
  nowMs = Date.now()
): RangeHorizon | undefined => {
  const start = toEpochMs(range?.startTime);

  if (start === undefined) {
    return undefined;
  }

  const end = toEpochMs(range?.endTime) ?? nowMs;
  const days = (end - start) / MS_PER_DAY;

  if (days <= 0) {
    return undefined;
  }

  const [nearest] = HORIZON_DAYS.reduce((best, candidate) =>
    Math.abs(Math.log(days / candidate[1])) < Math.abs(Math.log(days / best[1]))
      ? candidate
      : best
  );

  return nearest;
};

export const countryMatchers = (country: Country): string[] => {
  const alpha2 = country.id.COUNTRY_ISOALPHA2 ?? country.id.ISOALPHA2;
  const alpha3 = country.id.COUNTRY_ISOALPHA3 ?? country.id.ISOALPHA3;
  const displayName = alpha2
    ? new Intl.DisplayNames(['en'], { type: 'region' }).of(alpha2.toUpperCase())
    : undefined;

  return [alpha2, alpha3, country.name, displayName]
    .filter((value): value is string => Boolean(value))
    .map(value => value.toLowerCase());
};
